import { theme } from '@kijiji/theme'
import { Flex, Spacing } from '@kijiji/ui'
import { useDecision } from '@optimizely/react-sdk'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'
import { useCallback } from 'react'

import { SrpResults } from '@/components/srp/results-sort/results'
import {
  SaveSearchWrapper,
  SrpResultsSortContainer,
  SrpSortContainer,
} from '@/components/srp/results-sort/styled'
import { SaveSearchChipButton } from '@/components/srp/save-search-chip'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { MapViewButton } from '@/features/map/components/map-view-button/MapViewButton'
import { RemoteParamKeys, useToggle } from '@/lib/firebase/hooks'
import { GA_SAVE_SEARCH_TYPES } from '@/lib/ga/constants/ga'
import { FEATURE_FLAG } from '@/lib/optimizely'
import { SelectVariant } from '@/ui/molecules/select'
import { getUserDevice } from '@/utils/getUserDevice'

const SrpSort = dynamic(() => import('./sort').then((mod) => mod.SrpSort), { ssr: false })

export type SrpResultsSortProps = {
  /** This prop should be used in order to indicates whether the results text should be shown or not */
  shouldRenderResultsSortText?: boolean
  setShouldRenderMapSRP?: React.Dispatch<React.SetStateAction<boolean>>
  shouldRenderMapSRP?: boolean
  isMapSRPEnabled?: boolean
  isMobile?: boolean
}

export const SrpResultsSort = ({
  shouldRenderResultsSortText = true,
  setShouldRenderMapSRP,
  shouldRenderMapSRP = false,
  isMapSRPEnabled = false,
  isMobile = false,
}: SrpResultsSortProps) => {
  const quickFilterPillsToggle = useToggle(RemoteParamKeys.SRP_QUICK_FILTERS)
  const areQuickFilterPillsActive = quickFilterPillsToggle?.enabled === true

  const [saveSearchWithinFiltersDecision] = useDecision(FEATURE_FLAG.SAVED_SEARCH_WITHIN_FILTERS)
  const { status: userSessionStatus } = useSession()
  const isMobileDevice = getUserDevice().isPhone

  const isSaveSearchBtnEnabled =
    saveSearchWithinFiltersDecision.enabled &&
    isUserAuthenticated(userSessionStatus) &&
    isMobileDevice

  const handleRenderMapView = useCallback(() => {
    setShouldRenderMapSRP?.((oldState) => !oldState)
  }, [setShouldRenderMapSRP])

  return (
    <SrpResultsSortContainer
      flexDirection="row"
      isSaveSearchActive={
        saveSearchWithinFiltersDecision.enabled && isUserAuthenticated(userSessionStatus)
      }
    >
      {isSaveSearchBtnEnabled && (
        <SaveSearchWrapper justifyContent="flex-start">
          <SaveSearchChipButton
            isPillVariant={areQuickFilterPillsActive}
            isSrpView
            eventTrackingSource={GA_SAVE_SEARCH_TYPES.btn}
          />
        </SaveSearchWrapper>
      )}
      {shouldRenderResultsSortText && <SrpResults showOnLargeBreakpoint />}
      <Flex
        flexGrow={1}
        gap={theme.spacing.defaultSmall}
        justifyContent="flex-end"
        alignItems="center"
      >
        {isMapSRPEnabled && (
          <Spacing
            mTop={theme.spacing.default}
            large={{
              mTop: '0',
            }}
          >
            <MapViewButton
              onClick={handleRenderMapView}
              type={shouldRenderMapSRP ? 'list' : 'map'}
            />
          </Spacing>
        )}
        {isMapSRPEnabled && isMobile ? (
          <></>
        ) : (
          <SrpSortContainer isSaveSearchActive={isSaveSearchBtnEnabled} isRoundedSelect={false}>
            <SrpSort
              variant={areQuickFilterPillsActive ? SelectVariant.ROUNDED : SelectVariant.DEFAULT}
            />
          </SrpSortContainer>
        )}
      </Flex>
    </SrpResultsSortContainer>
  )
}
